import type { AdminSelf } from '@saay/api-client';
import { useAuth } from '@saay/auth-web';
import { CardContent, ListRow, RowList, Surface } from '@saay/blocks';
import { statusLabel } from '@saay/formatting';
import { useLogin } from '@saay/web-app';
import { ChevronLeftIcon, LockIcon, MonitorIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { DataState, PageHeader, ProfileHeader, Skeleton } from '../kit.js';
import { useAdminSelf } from '../queries.js';
import { selfHasPassword } from '../sessions.js';

export function AdminSelfCard({ self }: { readonly self: AdminSelf }) {
  const roles = self.roles.map((role) => statusLabel('fixedRole', role)).join('، ');
  const { controller } = useAuth();
  const { hash, pathname, search } = useLocation();
  const rows: ReactNode[] = [
    <ListRow
      end={<ChevronLeftIcon aria-hidden="true" className="size-4 text-muted-foreground" />}
      icon={MonitorIcon}
      key="sessions"
      look="plain"
      render={<Link to="/sessions" />}
      title="دستگاه‌های فعال"
      tone="forest"
    />,
  ];
  {
    // AC §27.2: the same action creates the first password or changes it.
    rows.push(
      <ListRow
        icon={LockIcon}
        key="password"
        look="plain"
        render={
          <button
            onClick={() => void controller.startPasswordChange(`${pathname}${search}${hash}`)}
            type="button"
          />
        }
        title={selfHasPassword(self) ? 'تغییر رمز عبور' : 'ساخت رمز عبور'}
        tone="forest"
      />,
    );
  }
  return (
    <>
      <section aria-labelledby="me-profile" className="saay-card">
        <ProfileHeader
          headingId="me-profile"
          name={self.user.displayNameFa ?? 'بدون نام'}
          subtitle={roles}
        />
        <dl className="saay-facts">
          <dt>نقش</dt>
          <dd>{roles}</dd>
        </dl>
      </section>
      <nav aria-label="تنظیمات حساب" className="flex flex-col gap-3">
        <p className="text-sm font-bold text-muted-foreground">امنیت</p>
        <Surface className="py-0">
          <CardContent>
            <RowList>{rows}</RowList>
          </CardContent>
        </Surface>
      </nav>
    </>
  );
}

/** `/me`: the principal's self context (AC §17.4.1) with every FTD §11 state. */
export function MePage() {
  const self = useAdminSelf();
  const login = useLogin();

  return (
    <>
      <PageHeader title="حساب من" />
      <DataState
        error={self.error}
        onLogin={() => login()}
        onRetry={() => void self.refetch()}
        session
        skeleton={<Skeleton label="در حال بارگذاری حساب…" lines={5} />}
        status={self.status}
      >
        {self.data === undefined ? null : <AdminSelfCard self={self.data} />}
      </DataState>
    </>
  );
}
